import { useEffect, useRef, useState } from 'react'
import { HelpCircle, Loader2 } from 'lucide-react'
import { useUiStore } from '@/store/uiStore'
import { useValuationStore } from '@/store/valuationStore'
import { useFieldHelp } from '@/hooks/useFieldHelp'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { AiProcessModal } from '@/components/ui/AiProcessModel'
import { FIELD_VALIDATORS, INPUT_RESTRICTIONS } from '@/utils/fieldValidators'
import { decodeVin } from '@/services/vinService'
import { mapResponseToForm } from '@/utils/fieldResponseMapper'
import { ALL_SECTIONS, FIELD_LABEL_MAP } from '@/data/allSections'
import { cn } from '@/utils/cn'
import { sortSelectOptions } from '@/utils'
import type { FieldDefinition, FormValues, PhotoItem, FieldResponsePayload, MappedField } from '@/types'
import { getMessage } from '@/utils/messages'
import { VEHICLE_TYPE_SECTIONS } from '@/data/vehicleTypeSections'

const SOURCE_KEY_MAP: Record<string, string> = {
  'VIN':            'field_input.source_vin',
  'OCR':            'field_input.source_ocr',
  'AI':             'field_input.source_ai',
  'Form Recognizer':'field_input.source_form_recognizer',
  'Valuation Report': 'field_input.source_valuation_report',
  'Info Label':     'field_input.source_info_label',
}
function resolveSourceLabel(source: string): string | null {
  const key = SOURCE_KEY_MAP[source]
  return key ? getMessage(key as Parameters<typeof getMessage>[0]) : null
}

const isoToDisplay = (v: string) => {
  const m = v?.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[2]}/${m[3]}/${m[1]}` : v
}


interface FieldInputProps {
  field: FieldDefinition
  sectionId: string
  error?: string
}

// Returns true when any of the given sections contain filled field values or photos.
// Fields marked autoSet in fieldMetadata are excluded (they were set programmatically, not by the user).
function sectionsHaveData(
  sectionIds: string[],
  formValues: FormValues,
  photos: PhotoItem[],
  fieldMetadata: Record<string, Record<string, { autoSet?: boolean }>>
): boolean {
  return sectionIds.some((sid) => {
    const fields = formValues[sid] ?? {}
    const sectionMeta = fieldMetadata[sid] ?? {}
    const hasFields = Object.entries(fields).some(
      ([key, val]) =>
        key !== '_photoUrls' &&
        val !== '' &&
        !val.startsWith('Select') &&
        !sectionMeta[key]?.autoSet
    )
    const hasPhotos = photos.some((p) => p.sectionId === sid)
    return hasFields || hasPhotos
  })
}

// Sections affected by a use/type change
const BODY_TYPE_SECTIONS = ['body-information']

export function FieldInput({ field, sectionId, error }: FieldInputProps) {
  const { openHelp, activeHelpField, clearFieldApiError, showToast, setVinDecoding } = useUiStore()
  const {
    formValues,
    photos,
    fieldMetadata,
    aiResults,
    aiRawPayloads,
    setFieldValue,
    resetSectionFields,
    clearAllPhotos,
    clearSectionPhotos,
    hasTriedSubmit,
    setHasTriedSubmit,
    hasTriedSave,
    setHasTriedSave,
    clearAllAIData,
    clearBodyInfoAIData,
    clearScanResult,
    applyAIFields,
    status,
    vehicleSectionsDirty,
    clearVehicleSectionsDirty,
  } = useValuationStore()

  const isSubmitted = status === 'submitted'

  // formSection lets a field be rendered in one section's grid while its value lives in
  // another (see FieldDefinition.formSection) — used by the Use/Type Subtype field, which
  // renders in body-information but stores alongside Use/Type in standard-equipment.
  const targetSection = field.formSection ?? sectionId

  const responseMeta = fieldMetadata[targetSection]?.[field.id]

  const [isFocused, setIsFocused] = useState(false)
  const [vinMappedFields, setVinMappedFields] = useState<MappedField[]>([])
  const [vinModalOpen, setVinModalOpen] = useState(false)
  const lastVinAppliedRef = useRef<MappedField[] | null>(null)
  // Lazy-init: run validator against whatever is already stored (e.g. restored draft)
  const [fieldError, setFieldError] = useState<string | undefined>(() =>
    FIELD_VALIDATORS[field.id]?.(formValues[targetSection]?.[field.id] ?? '', formValues[targetSection])
  )

  const toModalFields = (fields: MappedField[]) =>
    fields.map((mf) => ({
      label: mf.label || FIELD_LABEL_MAP[mf.fieldKey] || mf.fieldKey,
      aiValue: isoToDisplay(mf.value),
      value: isoToDisplay(useValuationStore.getState().formValues[mf.sectionId]?.[mf.fieldKey] || 'NA'),
      source: mf.source,
      confidence: mf.confidence,
      sectionId: mf.sectionId,
    }))

  // Re-run validator when any sibling field in the same section changes (e.g. dateOfLoss
  // changing should re-validate dateClaimReceived without the user touching it).
  const sectionVals = formValues[targetSection]
  useEffect(() => {
    const validator = FIELD_VALIDATORS[field.id]
    if (!validator) return
    setFieldError(validator(sectionVals?.[field.id] ?? '', sectionVals))
  }, [sectionVals, field.id])

  const restriction = INPUT_RESTRICTIONS[field.id]

  // Block invalid keystrokes for restricted fields (digits-only)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!restriction?.digitsOnly) return
    // Allow: backspace, delete, tab, escape, arrows, home, end, ctrl/cmd combos
    if (
      ['Backspace', 'Delete', 'Tab', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) ||
      e.ctrlKey || e.metaKey
    ) return
    // Block anything that is not a digit
    if (!/^\d$/.test(e.key)) e.preventDefault()
  }

  // Block paste of non-digit content for restricted fields
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!restriction?.digitsOnly) return
    const pasted = e.clipboardData.getData('text')
    if (!/^\d+$/.test(pasted)) e.preventDefault()
  }

  // Pending confirmation state: stores the new value until the user confirms/cancels
  const [pendingValue, setPendingValue] = useState<string | null>(null)
  const [confirmFor, setConfirmFor] = useState<'vehicleType' | 'bodyType' | null>(null)

  const value = formValues[targetSection]?.[field.id] ?? ''
  const { loading: helpLoading, hasHelp } = useFieldHelp(field.id, value)
  const isHelpActive = activeHelpField === field.id
  const isTextarea = field.type === 'textarea'
  const isCheckbox = field.type === 'checkbox'
  const isToggle = field.type === 'toggle'

  const SAVE_REQUIRED_FIELDS = new Set(['claimRefNo', 'vehicleType'])
  const hasValue = value !== '' && !value.startsWith('Select')
  const isFloating = isFocused || hasValue || field.type === 'date' || field.type === 'select'
  const hasSubmitError = hasTriedSubmit && !!field.required && !hasValue
  const hasSaveError = hasTriedSave && SAVE_REQUIRED_FIELDS.has(field.id) && !hasValue
  // fieldError takes priority over the prop-level error; submit/save errors are also included
  const hasError = !!fieldError || !!error || hasSubmitError || hasSaveError
  const displayError = fieldError ?? error ?? (hasSubmitError || hasSaveError ? getMessage('field_validation.required', { field: field.label }) : undefined)

  // ── Apply the actual field change + downstream clears ───────────────────
  const applyVehicleTypeChange = (newValue: string, notify = true) => {
    clearFieldApiError(sectionId, field.id)
    setFieldValue(sectionId, field.id, newValue)
    VEHICLE_TYPE_SECTIONS.forEach((sid) => resetSectionFields(sid))
    clearAllPhotos()
    clearAllAIData()
    setHasTriedSubmit(false)
    clearVehicleSectionsDirty()
    if (notify) showToast(getMessage('vehicle.info.vehicle_type_changed'), 'info')
  }

  const applyBodyTypeChange = (newValue: string, notify = true) => {
    clearFieldApiError(sectionId, field.id)
    setFieldValue(sectionId, field.id, newValue)
    resetSectionFields('body-information')
    clearSectionPhotos('body-information')
    clearBodyInfoAIData()
    if (notify) showToast(getMessage('vehicle.info.body_type_changed'), 'info')
  }

  // ── onChange: intercept destructive changes ──────────────────────────────
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    let newValue = e.target.value

    // Strip leading whitespace for text/textarea inputs so they can never start with a space.
    if (field.type === 'text' || field.type === 'textarea') {
      newValue = newValue.trimStart()
    }

    if (field.id === 'vehicleType') {
      if (vehicleSectionsDirty) {
        // Park the new value and show confirmation — don't update store yet.
        // The controlled <select value={value}> will visually revert on re-render.
        setPendingValue(newValue)
        setConfirmFor('vehicleType')
        return
      }
      applyVehicleTypeChange(newValue, false)
      return
    }

    if (field.id === 'use' || field.id === 'type') {
      const bodyInfoHasAIData =
        Object.keys(fieldMetadata['body-information'] ?? {}).length > 0 ||
        Object.values(aiResults).some((fields) => fields.some((f) => f.sectionId === 'body-information')) ||
        Object.keys(aiRawPayloads).length > 0
      if (sectionsHaveData(BODY_TYPE_SECTIONS, formValues, photos, fieldMetadata) || bodyInfoHasAIData) {
        setPendingValue(newValue)
        setConfirmFor('bodyType')
        return
      }
      applyBodyTypeChange(newValue, false)
      return
    }

    clearFieldApiError(targetSection, field.id)
    setFieldValue(targetSection, field.id, newValue)
    if (hasTriedSave) {
      const updatedValues = { ...formValues['basic-information'], [field.id]: newValue }
      const allSaveFilled = [...SAVE_REQUIRED_FIELDS].every(
        (fid) => updatedValues[fid] && !updatedValues[fid].startsWith('Select')
      )
      if (allSaveFilled) setHasTriedSave(false)
    }
    if (responseMeta?.scanResult) clearScanResult(targetSection, field.id)
    const validationError = FIELD_VALIDATORS[field.id]?.(newValue, formValues[targetSection])
    setFieldError(validationError)

    if (field.id === 'dateOfLoss' && !newValue) {
      setFieldValue(targetSection, 'dateClaimReceived', '')
    }

    if (field.id === 'vin') {
      if (validationError || !newValue) {
        if (lastVinAppliedRef.current) {
          lastVinAppliedRef.current.forEach((f) => setFieldValue(f.sectionId, f.fieldKey, ''))
          lastVinAppliedRef.current = null
        }
        setVinMappedFields([])
        return
      }

      if (newValue.length === 17) {
        setVinDecoding(true)
        decodeVin(newValue)
          .then((result) => {
            if (!result.success || !result.data) {
              showToast(result.message ?? getMessage('vin.error.decode_failed'), 'error')
              return
            }
            const vinSections = (result.data as { sections?: { section: string; fields: Record<string, { value: unknown; confidence: number; source: string }> }[] })?.sections ?? []
            const payload: FieldResponsePayload = {
              sections: Object.fromEntries(
                vinSections.map(({ section, fields }) => [
                  section,
                  {
                    section,
                    fields: Object.fromEntries(
                      Object.entries(fields).map(([key, entry]) => [
                        key,
                        { value: String(entry.value ?? ''), confidence: entry.confidence ?? 1, source: entry.source },
                      ])
                    ),
                  },
                ])
              ),
            }
            const { mappedFields } = mapResponseToForm(payload, ALL_SECTIONS)
            const filtered = mappedFields.filter((f) => f.value && f.value.trim() !== '' && f.value.trim() !== 'No Data Found')
            if (filtered.length > 0) {
              setVinMappedFields(filtered)
              setVinModalOpen(true)
            }
          })
          .catch((err: unknown) => {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? getMessage('vin.error.decode_failed_retry')
            showToast(message, 'error')
          })
          .finally(() => setVinDecoding(false))
      }
    }
  }

  // ── Confirm: apply pending change ────────────────────────────────────────
  const handleConfirm = () => {
    if (pendingValue === null) return
    if (confirmFor === 'vehicleType') applyVehicleTypeChange(pendingValue)
    else if (confirmFor === 'bodyType') applyBodyTypeChange(pendingValue)
    setPendingValue(null)
    setConfirmFor(null)
  }

  // ── Cancel: discard pending value (controlled input already reverted) ────
  const handleCancel = () => {
    setPendingValue(null)
    setConfirmFor(null)
  }

  const dateInputRef = useRef<HTMLInputElement>(null)
  const todayStr = new Date().toISOString().split('T')[0]

  // ── Floating-label input wrapper ─────────────────────────────────────────
  const renderFloatingInput = () => {
    const sharedFocusProps = {
      onFocus: () => setIsFocused(true),
      onBlur: () => {
        setIsFocused(false)
        if (field.type === 'text' || field.type === 'textarea') {
          const trimmed = value.trim()
          if (trimmed !== value) {
            clearFieldApiError(sectionId, field.id)
            setFieldValue(sectionId, field.id, trimmed)
            if (trimmed === '' && value !== '') {
              setFieldError(getMessage('field_validation.whitespace_only'))
            }
          }
        }
      },
    }

    const labelEl = (
      <label
        className={cn(
          'floating-label',
          isTextarea && 'textarea-label',
          isFloating && 'label-float-up',
        )}
      >
        {field.label}
        {field.required && (
          <span className="ml-[2px]" style={{ color: hasError ? 'var(--error)' : 'var(--accent2)' }}>*</span>
        )}
      </label>
    )

    if (field.type === 'select') {
      return (
        <div className="floating-wrapper">
          <select
            className={cn('field-input floating-input', hasError && 'error', isSubmitted && 'opacity-60 cursor-not-allowed')}
            value={value}
            onChange={handleChange}
            disabled={isSubmitted}
            data-testid={`field-${field.id}`}
            {...sharedFocusProps}
          >
            {sortSelectOptions(field.options ?? []).map((opt, index) => (
              <option key={opt} value={index === 0 ? '' : opt}>
                {opt}
              </option>
            ))}
          </select>
          {labelEl}
        </div>
      )
    }

    if (field.type === 'textarea') {
      return (
        <div className="floating-wrapper">
          <textarea
            className={cn('field-input floating-input', hasError && 'error', isSubmitted && 'opacity-60 cursor-not-allowed')}
            value={value}
            onChange={handleChange}
            readOnly={isSubmitted}
            rows={3}
            data-testid={`field-${field.id}`}
            {...sharedFocusProps}
          />
          {labelEl}
        </div>
      )
    }

    if (field.type === 'date') {
      const minDate = field.id === 'dateClaimReceived'
        ? (formValues[sectionId]?.['dateOfLoss'] || undefined)
        : undefined
      return (
        <div
          className={cn('floating-wrapper cursor-pointer', isSubmitted && 'cursor-not-allowed')}
          onClick={() => {
            if (isSubmitted) return
            dateInputRef.current?.showPicker?.()
            dateInputRef.current?.focus()
          }}
        >
          <input
            ref={dateInputRef}
            type="date"
            className={cn('field-input floating-input cursor-pointer', hasError && 'error', isSubmitted && 'opacity-60 cursor-not-allowed')}
            value={value}
            onChange={handleChange}
            readOnly={isSubmitted}
            min={minDate}
            max={todayStr}
            data-testid={`field-${field.id}`}
            {...sharedFocusProps}
          />
          {labelEl}
        </div>
      )
    }

    return (
      <div className="floating-wrapper">
        <input
          type={field.type}
          className={cn('field-input floating-input', hasError && 'error', isSubmitted && 'opacity-60 cursor-not-allowed')}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          readOnly={isSubmitted}
          maxLength={restriction?.maxLength}
          data-testid={`field-${field.id}`}
          {...sharedFocusProps}
        />
        {labelEl}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col',
        field.span === 2 && 'col-span-full'
      )}
    >
      {isCheckbox && (
        <label className="flex items-center gap-[5px] text-[11px] font-semibold text-[var(--text-dim)] tracking-[0.6px] mb-[5px]">
          {field.label}
          {field.required && (
            <span className="text-[var(--accent2)] text-[12px] leading-none">*</span>
          )}
        </label>
      )}

      <div className={cn('flex items-stretch gap-[6px]', (isTextarea || isCheckbox) && 'items-start')}>
        {isCheckbox ? (
          <div
            className="flex flex-wrap gap-x-4 gap-y-2 pt-1"
            data-testid={`field-${field.id}`}
          >
            {field.checkboxOptions?.map((opt) => {
              const checked = value ? value.split(',').map(v => v.trim()).includes(opt) : false
              const handleCheckbox = () => {
                if (isSubmitted) return
                const current = value ? value.split(',').map(v => v.trim()).filter(Boolean) : []
                const updated = checked ? current.filter(v => v !== opt) : [...current, opt]
                setFieldValue(sectionId, field.id, updated.join(', '))
              }
              return (
                <label key={opt} className={cn('flex items-center gap-2 group', isSubmitted ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
                  <div
                    onClick={handleCheckbox}
                    className={cn(
                      'w-4 h-4 rounded-[4px] border flex-shrink-0',
                      'flex items-center justify-center',
                      'transition-all duration-150 cursor-pointer',
                      checked
                        ? 'bg-[var(--accent)] border-[var(--accent)]'
                        : 'bg-[var(--surface2)] border-[var(--border)] group-hover:border-[var(--accent)]'
                    )}
                  >
                    {checked && (
                      <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                        <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className="text-[12px] font-medium text-[var(--text-dim)]">{opt}</span>
                </label>
              )
            })}
          </div>
        ) : isToggle ? (
          <div className="toggle-wrapper" data-testid={`field-${field.id}`}>
            <label className="floating-label label-float-up">
              {field.label}
              {field.required && (
                <span className="ml-[2px]" style={{ color: 'var(--accent2)' }}>*</span>
              )}
            </label>
            <button
              type="button"
              role="checkbox"
              aria-checked={value === 'true'}
              disabled={isSubmitted}
              onClick={() => { if (!isSubmitted) setFieldValue(sectionId, field.id, value === 'true' ? 'false' : 'true') }}
              className={cn(
                'flex items-center gap-[8px] text-[12px] font-semibold',
                'bg-transparent border-none p-0 transition-colors duration-150',
                isSubmitted ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                value === 'true' ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'
              )}
            >
              <div
                className={cn(
                  'w-4 h-4 rounded-[4px] border flex items-center justify-center flex-shrink-0 transition-all duration-150',
                  value === 'true'
                    ? 'bg-[var(--accent)] border-[var(--accent)]'
                    : 'bg-white border-[var(--border)]'
                )}
              >
                {value === 'true' && (
                  <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                    <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              {value === 'true' ? getMessage('common.button.yes') : getMessage('common.button.no')}
            </button>
          </div>
        ) : (
          renderFloatingInput()
        )}

        {helpLoading ? (
          <div
            aria-hidden="true"
            data-testid={`field-help-loading-${field.id}`}
            className={cn(
              'w-[30px] flex-shrink-0 rounded-[var(--radius-sm)]',
              'border border-[var(--border)] bg-[var(--surface3)]',
              'text-[var(--text-muted)] flex items-center justify-center',
              isTextarea ? 'h-[74px] mt-[10px]' : isCheckbox ? 'self-start mt-1 h-[34px]' : 'h-[42px] mt-[10px]'
            )}
          >
            <Loader2 size={14} strokeWidth={2} className="animate-spin" />
          </div>
        ) : (
          hasHelp && (
            <button
              type="button"
              onClick={() => openHelp(field.id, sectionId)}
              data-field={field.id}
              className={cn(
                'w-[30px] flex-shrink-0 rounded-[var(--radius-sm)]',
                'border border-[var(--border)] bg-[var(--surface3)]',
                'text-[var(--text-muted)] cursor-pointer',
                'flex items-center justify-center transition-all duration-[180ms]',
                isTextarea ? 'h-[74px] mt-[10px]' : isCheckbox ? 'self-start mt-1 h-[34px]' : 'h-[42px] mt-[10px]',
                isHelpActive
                  ? 'bg-[var(--accent)] border-[var(--accent)] text-white'
                  : 'hover:bg-[var(--accent)] hover:border-[var(--accent)] hover:text-white'
              )}
              title={getMessage('field_input.tooltip_help')}
            >
              <HelpCircle size={14} strokeWidth={2} />
            </button>
          )
        )}
      </div>

      {displayError && (
        <span className="text-[12px] text-[var(--error)] mt-[2px]">
          {displayError}
        </span>
      )}

      {(responseMeta && !responseMeta.hideConfidence && !responseMeta.scanResult && value !== '' && !value.startsWith('Select') && (responseMeta.aiValue === undefined || value === responseMeta.aiValue || (value === '' && responseMeta.aiValue === 'false'))) || responseMeta?.scanResult ? (
        <div className="flex items-center mt-[5px] gap-[6px] flex-wrap">
          {!responseMeta.hideConfidence && !responseMeta.scanResult && (() => {
            const sourceLabel = responseMeta.source ? resolveSourceLabel(responseMeta.source) : null
            if (!sourceLabel) return null
            const isVin = sourceLabel === 'VIN'
            return (
              <>
                <span className="text-[12px] font-bold px-[8px] py-[3px] rounded-[5px] border text-blue-700 bg-blue-50 border-blue-200">
                  {getMessage('field_input.source_label_prefix')} {sourceLabel}
                </span>
                {!isVin && (
                  <span className={cn(
                    'text-[12px] font-bold px-[8px] py-[3px] rounded-[5px] border',
                    (() => {
                      const pct = responseMeta.confidence * 100
                      if (pct < 70) return 'text-red-700 bg-red-50 border-red-200'
                      if (pct < 90) return 'text-yellow-700 bg-yellow-50 border-yellow-200'
                      return 'text-green-700 bg-green-50 border-green-200'
                    })()
                  )}>
                    {getMessage('field_input.confidence_prefix')} {(responseMeta.confidence * 100).toFixed(0)}%
                  </span>
                )}
              </>
            )
          })()}

          {responseMeta?.scanResult && (() => {
            const scanSourceLabel = responseMeta.source ? resolveSourceLabel(responseMeta.source) : null
            return (
              <>
                {scanSourceLabel && (
                  <span className="text-[12px] font-bold px-[8px] py-[3px] rounded-[5px] border text-blue-700 bg-blue-50 border-blue-200">
                    {getMessage('field_input.source_label_prefix')} {scanSourceLabel}
                  </span>
                )}
                <span className="text-[12px] font-bold px-[8px] py-[3px] rounded-[5px] border text-green-700 bg-green-50 border-green-200">
                  {getMessage('field_input.scan_result')} {responseMeta.scanResult}
                </span>
              </>
            )
          })()}
        </div>
      ) : null}

      {/* ── Confirmation dialogs ─────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmFor === 'vehicleType'}
        title={getMessage('vehicle.confirm.vehicle_type_title')}
        message={getMessage('vehicle.confirm.vehicle_type_body')}
        confirmLabel={getMessage('common.button.yes')}
        cancelLabel={getMessage('common.button.no')}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <ConfirmDialog
        open={confirmFor === 'bodyType'}
        title={getMessage('vehicle.confirm.body_type_title')}
        message={getMessage('vehicle.confirm.body_type_body')}
        confirmLabel={getMessage('common.button.yes')}
        cancelLabel={getMessage('common.button.no')}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* ── VIN decode results modal ─────────────────────────────────────── */}
      <AiProcessModal
        open={vinModalOpen}
        title={getMessage('ocr.modal.vin_results_title')}
        fields={toModalFields(vinMappedFields)}
        hideConfidence
        onContinue={() => {
          applyAIFields(vinMappedFields, true)
          lastVinAppliedRef.current = vinMappedFields
          setVinModalOpen(false)
          setVinMappedFields([])
        }}
        onCancel={() => {
          setVinModalOpen(false)
          setVinMappedFields([])
        }}
      />
    </div>
  )
}
